import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, User, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import { useUserStore } from '../../zustand/UserStore';
import { getApiBaseUrl } from '../../utils/config';
import { io, Socket } from 'socket.io-client';
import SupportRatingModal from '../SupportRatingModal';

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
  sender_name?: string;
  image_url?: string;
}

const defaultBotMessage: Message = {
  id: '1',
  text: "Hello! I'm your sales call analysis assistant. I'll connect you with our support team. How can I assist you today?",
  isBot: true,
  timestamp: new Date()
};

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([defaultBotMessage]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'reconnecting'>('disconnected');
  const [isLoadingTicket, setIsLoadingTicket] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [resolvedTicketId, setResolvedTicketId] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const hasLoadedTicket = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDarkMode = useTheme((state) => state.theme);
  const user = useUserStore((state) => state.user);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const refreshMessages = async () => {
    if (!ticketId) return;
    
    setIsRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      
      const response = await fetch(`${apiBaseUrl}/api/v1/support/tickets/${ticketId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const updatedTicket = await response.json();
        
        if (updatedTicket && updatedTicket.messages) {
          // Rebuild messages from ticket
          const refreshedMessages: Message[] = [defaultBotMessage];
          
          updatedTicket.messages.forEach((msg: any) => {
            refreshedMessages.push({
              id: msg.id || msg.timestamp,
              text: msg.message,
              isBot: msg.is_admin,
              timestamp: new Date(msg.timestamp),
              sender_name: msg.sender_name,
              image_url: msg.image_url
            });
          });
          
          setMessages(refreshedMessages);
          console.log('Messages refreshed successfully');
        }
      } else {
        console.error('Failed to refresh messages');
      }
    } catch (error) {
      console.error('Error refreshing messages:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchLastUnClosedTicket = async () => {
    try {
      setIsLoadingTicket(true);
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      
      const response = await fetch(`${apiBaseUrl}/api/v1/support/tickets/last-unclosed`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const ticket = await response.json();
        
        if (ticket && ticket.id) {
          console.log('Found existing unclosed ticket:', ticket.id);
          setTicketId(ticket.id);
          
          // Load existing messages from the ticket
          const ticketMessages: Message[] = [defaultBotMessage];
          
          if (ticket.messages && Array.isArray(ticket.messages)) {
            ticket.messages.forEach((msg: any) => {
              ticketMessages.push({
                id: msg.id || msg.timestamp,
                text: msg.message,
                isBot: msg.is_admin,
                timestamp: new Date(msg.timestamp),
                sender_name: msg.sender_name,
                image_url: msg.image_url
              });
            });
          }
          
          setMessages(ticketMessages);
          
          // Connect to socket for this ticket
          if (!socketRef.current) {
            connectSocket(ticket.id);
          }
          
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error fetching last unclosed ticket:', error);
      return false;
    } finally {
      setIsLoadingTicket(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Fetch last unclosed ticket when chat is opened for the first time
    if (isOpen && !hasLoadedTicket.current && user) {
      hasLoadedTicket.current = true;
      fetchLastUnClosedTicket();
    }
  }, [isOpen, user]);

  useEffect(() => {
    // Connect to Socket.IO if ticket already exists (when component mounts)
    if (ticketId && !socketRef.current) {
      connectSocket(ticketId);
    }
    
    // Cleanup Socket.IO on unmount
    return () => {
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnectionStatus('disconnected');
      }
    };
  }, [ticketId]);

  useEffect(() => {
    // Re-establish Socket.IO connection when chatbot is opened and we have a ticket but no connection
    if (isOpen && ticketId && !socketRef.current) {
      connectSocket(ticketId);
    }
  }, [isOpen, ticketId]);

  const createTicket = async (initialMessage: string): Promise<string | null> => {
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/v1/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ initial_message: initialMessage }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.ticket_id;
      }
      return null;
    } catch (error) {
      console.error('Error creating ticket:', error);
      return null;
    }
  };

  const connectSocket = (ticketId: string, retryCount = 0) => {
    // Clean up existing connection if any
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const token = localStorage.getItem('token');
    const apiBaseUrl = getApiBaseUrl();
    // Socket.IO should connect to the base URL without /api/v1 path
    const socketUrl = apiBaseUrl.replace('/api/v1', '');
    
    console.log('Frontend connecting to Socket.IO:', socketUrl);
    console.log('Retry count:', retryCount);
    
    setConnectionStatus('connecting');
    
    // Initialize Socket.IO client
    socketRef.current = io(socketUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: false, // Manual reconnection
      autoConnect: true
    });

    socketRef.current.on('connect', () => {
      setConnectionStatus('connected');
      console.log('✅ Socket.IO connected successfully');
      
      // Join the ticket room
      socketRef.current?.emit('join_ticket', {
        ticket_id: ticketId,
        token: token,
        is_admin: false
      });
      
      // Start ping interval to keep connection alive
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      pingIntervalRef.current = window.setInterval(() => {
        if (socketRef.current?.connected) {
          socketRef.current.emit('ping');
        }
      }, 30000); // Ping every 30 seconds
    });

    socketRef.current.on('joined_ticket', (data) => {
      console.log('✅ Successfully joined ticket:', data);
    });

    socketRef.current.on('new_message', (data) => {
      try {
        console.log('📨 Received message:', data);
        
        const messageId = data.id || data.timestamp || `msg-${Date.now()}`;
        
        // Handle special system messages (like ticket resolved notification)
        if (data.type === 'system_message') {
          console.log('🔔 System message received:', data.message);
          
          const systemMessage: Message = {
            id: messageId,
            text: data.message,
            isBot: true,
            timestamp: new Date(data.timestamp),
            sender_name: data.sender_name || 'System'
          };
          
          setMessages(prev => [...prev, systemMessage]);
          
          // If it's a resolved/closed status, show rating modal after a delay
          if (data.status === 'resolved' || data.status === 'closed') {
            setTimeout(() => {
              console.log('✅ Ticket resolved/closed, showing rating modal...');
              
              // Store the resolved ticket ID for rating
              setResolvedTicketId(ticketId);
              
              // Show rating modal
              setShowRatingModal(true);
              
              // Disconnect socket
              if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
                socketRef.current = null;
              }
              
              // Clear ticket ID so a new ticket can be created
              setTicketId(null);
              
              // Close the chat interface
              setIsOpen(false);
              
              // Reset messages
              setMessages([defaultBotMessage]);
              
              // Clear ping interval
              if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
              }
              
              setConnectionStatus('disconnected');
            }, 3000); // Give user 3 seconds to read the message
          }
          
          return;
        }
        
        const newMessage: Message = {
          id: messageId,
          text: data.message,
          isBot: data.is_admin, // Admin messages show as bot messages in user chat
          timestamp: new Date(data.timestamp),
          sender_name: data.sender_name,
          image_url: data.image_url
        };
        
        // Only add message if it's not from the current user (avoid duplicates)
        if (data.sender_id !== user?.uid) {
          setMessages(prev => {
            // Check for duplicates by ID
            const existsById = prev.some(msg => msg.id === messageId);
            if (existsById) {
              console.log('⏭️ Skipping duplicate message by ID');
              return prev;
            }
            
            // Check for duplicate content
            const duplicateIndex = prev.findIndex(msg => {
              const isSameText = msg.text === newMessage.text;
              const timeDiff = Math.abs(msg.timestamp.getTime() - newMessage.timestamp.getTime());
              const isRecent = timeDiff < 3000; // 3 seconds
              
              return isSameText && isRecent;
            });
            
            if (duplicateIndex !== -1) {
              console.log('⏭️ Skipping duplicate message by content');
              return prev;
            }
            
            console.log('✅ Adding new message');
            return [...prev, newMessage];
          });
        }
      } catch (error) {
        console.error('❌ Error processing message:', error);
      }
    });

    socketRef.current.on('message_sent', (data) => {
      console.log('✅ Message sent acknowledgment:', data);
    });

    socketRef.current.on('typing_indicator', (data) => {
      if (data.sender_id !== user?.uid) {
        setIsTyping(data.is_typing);
      }
    });

    socketRef.current.on('status_update', (data) => {
      console.log('📊 Ticket status updated:', data);
      // Status update received - the system_message will handle closing the chat
    });

    socketRef.current.on('pong', () => {
      console.log('🏓 Received pong from server');
    });

    socketRef.current.on('error', (error) => {
      console.error('❌ Socket.IO error:', error);
      setConnectionStatus('disconnected');
    });

    socketRef.current.on('disconnect', (reason) => {
      setConnectionStatus('disconnected');
      console.log('❌ Socket.IO disconnected:', reason);
      
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      // Retry connection if it was unexpected closure and we haven't exceeded retry limit
      if (reason !== 'io client disconnect' && retryCount < 3 && ticketId) {
        console.log(`🔄 Retrying Socket.IO connection (attempt ${retryCount + 1}/3)...`);
        setConnectionStatus('reconnecting');
        setTimeout(() => {
          connectSocket(ticketId, retryCount + 1);
        }, Math.pow(2, retryCount) * 1000); // Exponential backoff: 1s, 2s, 4s
      }
    });
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPG, PNG, GIF, WebP, BMP)');
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Image size must be less than 5MB');
      return;
    }

    setSelectedImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!selectedImage || !ticketId) return null;

    setIsUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      
      const formData = new FormData();
      formData.append('file', selectedImage);

      const response = await fetch(`${apiBaseUrl}/api/v1/support/tickets/${ticketId}/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      return data.image_url;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
      return null;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const sendMessage = (message: string, imageUrl?: string) => {
    if (!socketRef.current || !socketRef.current.connected) {
      console.warn('❌ Socket.IO is not connected');
      return false;
    }
    
    const messageData: any = {
      message: message
    };
    
    if (imageUrl) {
      messageData.image_url = imageUrl;
    }
    
    socketRef.current.emit('send_message', messageData);
    
    console.log('📤 Message sent to server');
    return true;
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() && !selectedImage) return;

    // Upload image first if selected
    let imageUrl: string | undefined;
    if (selectedImage && ticketId) {
      imageUrl = await uploadImage() || undefined;
      if (!imageUrl) {
        // Upload failed, don't send message
        return;
      }
    }

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: inputValue || '📷 Image',
      isBot: false,
      timestamp: new Date(),
      image_url: imageUrl
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = inputValue || '📷 Image';
    setInputValue('');
    clearImage();

    // If no ticket exists, create one
    if (!ticketId) {
      const newTicketId = await createTicket(messageText);
      if (newTicketId) {
        setTicketId(newTicketId);
        
        // Connect Socket.IO
        connectSocket(newTicketId);
      } else {
        // Fallback error message
        setIsTyping(true);
        setTimeout(() => {
          const botResponse: Message = {
            id: `error-${Date.now()}`,
            text: "I'm sorry, I'm having trouble connecting to our support system. Please try again later.",
            isBot: true,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, botResponse]);
          setIsTyping(false);
        }, 1500);
      }
    } else {
      // Send message through Socket.IO if already connected
      if (!sendMessage(messageText, imageUrl)) {
        // Connection lost, try to reconnect
        console.log('🔄 Connection lost, attempting to reconnect...');
        connectSocket(ticketId);
        
        // Show error feedback
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          text: "Connection lost. Attempting to reconnect...",
          isBot: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    // Shift+Enter will allow natural newline behavior
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    
    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    
    // Disconnect Socket.IO when closing chat
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setConnectionStatus('disconnected');
    }
    
    // Note: We don't reset hasLoadedTicket here so it doesn't reload on every open/close
    // The ticket and messages remain so the conversation can continue
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-500';
      case 'connecting': 
      case 'reconnecting': return 'text-yellow-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Response time: 1 to 3 hrs';
      case 'connecting': return 'Connecting...';
      case 'reconnecting': return 'Reconnecting...';
      default: return 'Response time: 1 to 3 hrs';
    }
  };

  return (
    <>
      {/* Support Rating Modal */}
      {showRatingModal && resolvedTicketId && (
        <SupportRatingModal
          isOpen={showRatingModal}
          onClose={() => {
            setShowRatingModal(false);
            setResolvedTicketId(null);
          }}
          ticketId={resolvedTicketId}
        />
      )}

      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg transition-all duration-300 hover:scale-110 ${
          isDarkMode 
            ? 'bg-[#1a9f6b] hover:bg-[#168a5c]' 
            : 'bg-[#1a9f6b] hover:bg-[#148856]'
        } ${isOpen ? 'hidden' : 'flex'} items-center justify-center`}
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className={`fixed bottom-6 right-6 z-50 w-96 h-[500px] rounded-2xl shadow-2xl border transition-all duration-300 flex flex-col ${
          isDarkMode 
            ? 'bg-[#0a1414] border-[#1a2424]' 
            : 'bg-white border-[#e0e0e0]'
        }`}>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b rounded-t-2xl ${
            isDarkMode 
              ? 'bg-[#0f1919] border-[#1a2424]' 
              : 'bg-[#f8f9fa] border-[#e0e0e0]'
          }`}>
          <div className={`flex items-center justify-between  w-full gap-3 ${
            isDarkMode ? 'bg-[#0f1919] border-[#1a2424]' : 'bg-[#f8f9fa] border-[#e0e0e0]'
          }`}>
            <div className="flex items-center gap-3 ">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-[#1a9f6b]' : 'bg-[#1a9f6b]'
              }`}>
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold text-sm ${
                  isDarkMode ? 'text-white' : 'text-[#053333]'
                }`}>
                  Sales Support
                </h3>
                <p className={`text-xs ${getStatusColor()}`}>
                  {getStatusText()}
                </p>
              </div>
            </div>
            <div>
            <button
              onClick={refreshMessages}
              disabled={isRefreshing || !ticketId}
              className={`p-1.5 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'hover:bg-[#1a2424] text-gray-400 hover:text-white' 
                  : 'hover:bg-[#e0e0e0] text-gray-600 hover:text-[#053333]'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              title="Refresh messages"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleClose}
              className={`p-1 rounded-full transition-colors ${
                isDarkMode 
                  ? 'hover:bg-[#1a2424] text-gray-400 hover:text-white' 
                  : 'hover:bg-[#e0e0e0] text-gray-600 hover:text-[#053333]'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
            </div>
          </div>
        </div>          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto space-y-4">
            {isLoadingTicket && (
              <div className="flex justify-center items-center h-full">
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Loading conversation...
                </div>
              </div>
            )}
            
            {!isLoadingTicket && messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
              >
                <div className={`flex items-end gap-2 max-w-[80%] ${
                  message.isBot ? 'flex-row' : 'flex-row-reverse'
                }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.isBot 
                      ? isDarkMode ? 'bg-[#1a9f6b]' : 'bg-[#1a9f6b]'
                      : isDarkMode ? 'bg-[#2a3a3a]' : 'bg-[#e0e0e0]'
                  }`}>
                    {message.isBot ? (
                      <Bot className="w-3 h-3 text-white" />
                    ) : (
                      <User className={`w-3 h-3 ${
                        isDarkMode ? 'text-gray-300' : 'text-[#053333]'
                      }`} />
                    )}
                  </div>
                  <div>
                    <div className={`rounded-2xl px-4 py-2 ${
                      message.isBot
                        ? isDarkMode 
                          ? 'bg-[#1a2424] text-white' 
                          : 'bg-[#f1f3f4] text-[#053333]'
                        : isDarkMode
                          ? 'bg-[#1a9f6b] text-white'
                          : 'bg-[#1a9f6b] text-white'
                    }`}>
                      {message.image_url && (
                        <div className="mb-2">
                          <img 
                            src={message.image_url} 
                            alt="Uploaded" 
                            className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(message.image_url, '_blank')}
                            style={{ maxHeight: '200px', objectFit: 'contain' }}
                          />
                        </div>
                      )}
                      <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{message.text}</p>
                    </div>
                    <p className={`text-xs mt-1 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-500'
                    } ${message.isBot ? 'text-left' : 'text-right'}`}>
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="flex items-end gap-2 max-w-[80%]">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isDarkMode ? 'bg-[#1a9f6b]' : 'bg-[#1a9f6b]'
                  }`}>
                    <Bot className="w-3 h-3 text-white" />
                  </div>
                  <div className={`rounded-2xl px-4 py-3 ${
                    isDarkMode ? 'bg-[#1a2424]' : 'bg-[#f1f3f4]'
                  }`}>
                    <div className="flex space-x-1">
                      <div className={`w-2 h-2 rounded-full animate-bounce ${
                        isDarkMode ? 'bg-gray-400' : 'bg-gray-500'
                      }`} style={{ animationDelay: '0ms' }}></div>
                      <div className={`w-2 h-2 rounded-full animate-bounce ${
                        isDarkMode ? 'bg-gray-400' : 'bg-gray-500'
                      }`} style={{ animationDelay: '150ms' }}></div>
                      <div className={`w-2 h-2 rounded-full animate-bounce ${
                        isDarkMode ? 'bg-gray-400' : 'bg-gray-500'
                      }`} style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={`p-4 border-t ${
            isDarkMode ? 'border-[#1a2424]' : 'border-[#e0e0e0]'
          }`}>
            {/* Image Preview */}
            {imagePreview && (
              <div className={`mb-3 p-3 rounded-lg border ${
                isDarkMode ? 'bg-[#0f1919] border-[#1a2424]' : 'bg-[#f8f9fa] border-[#e0e0e0]'
              }`}>
                <div className="flex items-start gap-3">
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <button
                      onClick={clearImage}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                      Image ready to send
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {selectedImage?.name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className={`flex items-center gap-2 p-3 rounded-xl border ${
              isDarkMode 
                ? 'bg-[#0f1919] border-[#1a2424]' 
                : 'bg-[#f8f9fa] border-[#e0e0e0]'
            }`}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp"
                onChange={handleImageSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingImage}
                className={`p-2 rounded-lg transition-colors ${
                  !isUploadingImage
                    ? isDarkMode
                      ? 'hover:bg-[#1a2424] text-gray-400 hover:text-white'
                      : 'hover:bg-[#e0e0e0] text-gray-600 hover:text-[#053333]'
                    : 'text-gray-500 cursor-not-allowed opacity-50'
                }`}
                title="Attach image"
              >
                <ImageIcon className="w-5 h-5" />
              </button>
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={
                  isUploadingImage 
                    ? "Uploading image..." 
                    : "Type your message... (Shift+Enter for new line)"
                }
                disabled={isUploadingImage}
                rows={1}
                className={`flex-1 bg-transparent outline-none text-sm resize-none overflow-hidden ${
                  isDarkMode ? 'text-white placeholder-gray-400' : 'text-[#053333] placeholder-gray-500'
                } disabled:opacity-50`}
                style={{ minHeight: '24px', maxHeight: '120px' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = '24px';
                  target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                }}
              />
              <button
                onClick={handleSendMessage}
                disabled={(!inputValue.trim() && !selectedImage) || isUploadingImage}
                className={`p-2 rounded-lg transition-all ${
                  (inputValue.trim() || selectedImage) && !isUploadingImage
                    ? isDarkMode
                      ? 'bg-[#1a9f6b] hover:bg-[#168a5c] text-white'
                      : 'bg-[#1a9f6b] hover:bg-[#148856] text-white'
                    : isDarkMode
                      ? 'bg-[#1a2424] text-gray-500 cursor-not-allowed'
                      : 'bg-[#e0e0e0] text-gray-400 cursor-not-allowed'
                }`}
              >
                {isUploadingImage ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}